/**
 * User Decision Preservation Framework (UD pack — UD1).
 *
 * Generic overlay-merger: when the platform regenerates a list of
 * items (SED conflicts, report sanity findings, evidence-strategy
 * rows, …) the user has already made decisions on the *prior*
 * version. Those decisions live on prior items keyed by stable
 * identity. This helper produces the new list with prior decisions
 * overlaid wherever identity still matches.
 *
 * The pattern is "fresh model output should never overwrite user
 * decisions; it should merge in the user's prior state by stable
 * identity". Applied four ways in the UD pack:
 *
 *   - UD2: SED conflict resolutions (`userResolution`)
 *   - UD4: sanity-finding resolutions (`resolution`, `userNote`,
 *     `applyDirective`, `resolvedAt`)
 *   - UD5: evidence-strategy + document inclusion / exclusion
 *   - LB3+UD6 audit overlap: the audit tuple lets the UI surface
 *     "N prior decisions preserved" badges.
 *
 * Pure — no Firestore, no GCS, no React. Side-effect-free + fully
 * deterministic so it can be unit-tested exhaustively. Callers
 * persist `merged` themselves and pipe `audit` into
 * `writePreservationAudit` for the paper trail.
 *
 * ─── Signature (kept stable for DM5 + future callers) ───────────
 *
 *   function mergePreservedUserDecisions<T extends Record<string, unknown>>(args: {
 *     prior: T[] | undefined;          // previous-version items (carry decisions)
 *     fresh: T[];                      // freshly-generated items (no decisions)
 *     identity: (item: T) => string;   // stable key (e.g. "sectionId::field::docId")
 *     preserveFields: readonly (keyof T)[];  // fields to overlay from prior → fresh
 *     onOrphan?: (priorItem: T) => void;     // optional callback for audit-log orphan tracking
 *   }): [merged: T[], audit: PreservedAudit[]]
 *
 * Identity strategy ladder (callers compose):
 *   - Strategy A (stable id):       identity: i => i.id
 *   - Strategy B (composite tuple): identity: i => `${i.section}::${i.severity}::${i.anchor ?? hash(i.title)}`
 *   - Strategy C (content hash):    identity: i => sha1(canonical(i))
 *
 * Strategy B is preferred when the LLM rephrases natural-language
 * titles between runs but the anchor / structural key stays put.
 *
 * `preserveFields` is the explicit allow-list of decision fields —
 * we never blanket-copy prior over fresh, because fresh values for
 * model-driven properties (severity, explanation, anchor positions,
 * …) MUST win.
 *
 * Orphan handling: a prior item whose identity doesn't appear in
 * `fresh` is *dropped*, never re-injected. This matches the spec —
 * a fresh-output that no longer surfaces a row means the underlying
 * issue is gone (e.g. doc removed, conflict no longer detected) and
 * the prior decision is obsolete. The optional `onOrphan` callback
 * fires for each dropped prior so callers can audit-log the prune.
 */

/**
 * Audit row produced for every prior decision that successfully
 * carried forward onto a fresh item. Callers write these to the
 * `auditEvents` collection via `writePreservationAudit` (UD7).
 */
export interface PreservedAudit {
  /** Stable identity (e.g. `"environmental::high::env.species"`). */
  identity: string;
  /** Which fields actually carried over — never empty. */
  preservedFields: string[];
  /** Original resolution timestamp from the prior item (when set). */
  resolvedAt?: string;
  /** Original resolver from the prior item (when set). */
  resolvedBy?: string;
  /** Set when the identity fn fell back to a content-hash strategy. */
  fallbackUsed?: boolean;
}

export interface MergePreservedUserDecisionsArgs<T extends Record<string, unknown>> {
  /** Prior-version items carrying user decisions; undefined / empty acts as no-op. */
  prior: readonly T[] | undefined;
  /** Newly-generated items (no decisions yet). */
  fresh: readonly T[];
  /** Stable identity for an item. Must be deterministic across reruns. */
  identity: (item: T) => string;
  /**
   * Whitelist of fields to overlay from prior → fresh. Each listed
   * field is preserved only if its value on prior is `defined`
   * (not undefined, not null) — that's the marker for "user made
   * a decision". Empty strings are treated as decisions; null /
   * undefined as "no decision".
   */
  preserveFields: readonly (keyof T)[];
  /**
   * Fired once per prior item that did NOT find a matching fresh
   * item. Useful for orphan-prune audit events.
   */
  onOrphan?: (priorItem: T) => void;
}

/**
 * Returns a tuple of [merged, audit]:
 *   - `merged` — same shape as `fresh`, but each item that matched
 *     a prior by identity has the prior's `preserveFields`
 *     overlaid.
 *   - `audit` — one row per preserved decision so callers can write
 *     a paper trail.
 */
export function mergePreservedUserDecisions<T extends Record<string, unknown>>(
  args: MergePreservedUserDecisionsArgs<T>,
): [T[], PreservedAudit[]] {
  const { prior, fresh, identity, preserveFields, onOrphan } = args;

  // Materialize fresh first so we always return a real array even
  // when callers pass a readonly tuple / iterator. Type-narrow `T`
  // through the mapping below.
  const freshArr: T[] = fresh.map(f => ({ ...f }));

  if (!prior || prior.length === 0) return [freshArr, []];

  // Index prior by identity. Last-write-wins on duplicate keys —
  // shouldn't happen in practice but if it does, the most recent
  // prior wins because callers append to lists chronologically.
  const priorByKey = new Map<string, T>();
  for (const p of prior) {
    priorByKey.set(identity(p), p);
  }

  const matchedKeys = new Set<string>();
  const audit: PreservedAudit[] = [];

  const merged = freshArr.map(f => {
    const key = identity(f);
    const p = priorByKey.get(key);
    if (!p) return f;
    matchedKeys.add(key);

    // Build the overlay payload — only fields with a defined,
    // non-null value on prior carry forward. An undefined prior
    // field means "user never made a decision on this", in which
    // case the fresh slot (also undefined/null) stays as-is.
    const preservedKeys: string[] = [];
    const overlay: Partial<T> = {};
    for (const k of preserveFields) {
      const v = p[k];
      if (v !== undefined && v !== null) {
        overlay[k] = v;
        preservedKeys.push(String(k));
      }
    }
    if (preservedKeys.length === 0) return f;

    audit.push({
      identity: key,
      preservedFields: preservedKeys,
      resolvedAt: (p as { resolvedAt?: unknown }).resolvedAt as string | undefined,
      resolvedBy: (p as { resolvedBy?: unknown }).resolvedBy as string | undefined,
    });
    return { ...f, ...overlay };
  });

  // Fire onOrphan for prior items that had no fresh match.
  if (onOrphan) {
    for (const p of prior) {
      const k = identity(p);
      if (!matchedKeys.has(k)) {
        // Only fire when the prior carried at least one preserved
        // field — orphans of items with no decisions aren't
        // interesting to audit.
        const hadDecision = preserveFields.some(field => {
          const v = p[field];
          return v !== undefined && v !== null;
        });
        if (hadDecision) onOrphan(p);
      }
    }
  }

  return [merged, audit];
}

/**
 * Convenience: returns true when the framework would preserve at
 * least one field for the given prior/fresh pair under the supplied
 * identity. Helpful in callers that want to short-circuit a Firestore
 * write when nothing would change.
 */
export function wouldPreserveAny<T extends Record<string, unknown>>(
  args: Pick<MergePreservedUserDecisionsArgs<T>, 'prior' | 'fresh' | 'identity' | 'preserveFields'>,
): boolean {
  const [, audit] = mergePreservedUserDecisions(args);
  return audit.length > 0;
}

export const APPROVED_FACTUAL_CORRECTIONS_SCHEMA_VERSION = 'approved-factual-corrections-v1' as const;

export type ApprovedFactualCorrectionStatus =
  | 'active'
  | 'superseded-by-evidence'
  | 'withdrawn';

export interface EvidenceConflictDecisionV1 {
  conflictsWithPriorCorrection: boolean;
  reason?: string;
  changedFields?: string[];
  sourceIds?: string[];
}

export interface ApprovedFactualCorrectionEntryV1 {
  /** Stable finding identity (`section::category::fingerprint`). */
  identity: string;
  /** Monotonic per-entry version incremented whenever the user updates this correction. */
  version: number;
  section: string;
  category: string;
  finding: string;
  /** Canonical directive text approved by the user for prompt injection and re-evaluation. */
  appliedDirective: string;
  userNote?: string;
  applyDirective?: import('../types/siteRag').SanityFinding['applyDirective'];
  /** Evidence snippet captured when the correction was approved (informational context). */
  evidenceBaseline?: string;
  /** Traceable structured source/SED field values captured at approval time. */
  evidenceSourceFields?: Record<string, string | number | boolean | null>;
  /** Traceable source document / SED section identifiers captured at approval time. */
  evidenceSourceIds?: string[];
  /** Traceable source/SED revision identifier captured at approval time. */
  evidenceSourceRevision?: string | number;
  /** Optional SED fact paths anchored by this correction. */
  anchorFactPaths?: string[];
  approvedAt: string;
  approvedByUid?: string;
  originReportId?: string;
  lastAppliedReportId?: string;
  lastEvaluatedAt?: string;
  lastEvaluationStatus?: 'queued' | 'resolved' | 'still-failing' | 'superseded-by-evidence' | 'withdrawn';
  status: ApprovedFactualCorrectionStatus;
  withdrawnAt?: string;
  withdrawnByUid?: string;
  supersededReason?: string;
  supersededByEvidence?: string;
  changedSourceFields?: string[];
  conflictReason?: string;
}

export interface ApprovedFactualCorrectionsLedgerV1 {
  schemaVersion: typeof APPROVED_FACTUAL_CORRECTIONS_SCHEMA_VERSION;
  /** Monotonic ledger revision number bumped on every mutation or rerun reconciliation. */
  revision: number;
  updatedAt: string;
  entries: Record<string, ApprovedFactualCorrectionEntryV1>;
}

/**
 * Verified findings (`risk-verified` / `strength-verified`) are positive
 * confirmations. Their UI action is "Acknowledge" (`resolution: 'ignore'`),
 * NEVER a factual correction (`resolution: 'apply'`).
 */
export function isVerifiedSanityCategory(category: unknown): boolean {
  return category === 'risk-verified' || category === 'strength-verified';
}

export interface EvidenceSupersessionInput {
  baselineEvidence?: unknown;
  freshEvidence?: unknown;
  baselineSourceFields?: Record<string, string | number | boolean | null>;
  freshSourceFields?: Record<string, string | number | boolean | null>;
  baselineSourceIds?: readonly string[];
  freshSourceIds?: readonly string[];
  baselineSourceRevision?: string | number;
  freshSourceRevision?: string | number;
  conflictDecision?: EvidenceConflictDecisionV1;
}

export interface EvidenceSupersessionResult {
  conflicts: boolean;
  changedFields: string[];
  reason?: string;
}

function canonicalizeSourceFieldValue(value: unknown): string {
  if (value === null || value === undefined) return 'null';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : 'nan';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'string') return value.trim().replace(/\s+/g, ' ').toLowerCase();
  return JSON.stringify(value);
}

/**
 * Evaluates whether new source evidence genuinely supersedes a previously
 * approved factual correction.
 *
 * CRITICAL INVARIANT:
 * Free-text prose inequality in `finding.evidence` NEVER retires an approved
 * factual correction. Supersession requires BOTH:
 * 1. An explicit conflict decision (`conflictDecision.conflictsWithPriorCorrection === true`), AND
 * 2. A traceable source/field change (either structured `baselineSourceFields` vs
 *    `freshSourceFields` differ on at least one field key, or `conflictDecision.changedFields`
 *    identifies changed field keys backed by a traceable source ID or source revision change
 *    without being contradicted by identical structured field values).
 */
export function evaluateEvidenceSupersession(
  input: EvidenceSupersessionInput,
): EvidenceSupersessionResult {
  const decision = input.conflictDecision;
  if (!decision || decision.conflictsWithPriorCorrection !== true) {
    return { conflicts: false, changedFields: [] };
  }

  const baseFields = input.baselineSourceFields ?? {};
  const freshFields = input.freshSourceFields ?? {};
  const baseKeys = Object.keys(baseFields);
  const freshKeys = Object.keys(freshFields);

  const measuredDiffFields: string[] = [];
  if (baseKeys.length > 0 && freshKeys.length > 0) {
    const sharedKeys = baseKeys.filter(k => Object.prototype.hasOwnProperty.call(freshFields, k));
    for (const key of sharedKeys) {
      if (canonicalizeSourceFieldValue(baseFields[key]) !== canonicalizeSourceFieldValue(freshFields[key])) {
        measuredDiffFields.push(key);
      }
    }
  }

  const declaredChangedFields = Array.isArray(decision.changedFields)
    ? decision.changedFields
        .filter((f): f is string => typeof f === 'string' && f.trim().length > 0)
        .map(f => f.trim())
    : [];

  // If both baseline and fresh structured fields exist for all declared fields and none actually differ,
  // reject the conflict claim (guards against false conflict flags when underlying fields are unchanged).
  if (measuredDiffFields.length === 0 && baseKeys.length > 0 && freshKeys.length > 0) {
    if (declaredChangedFields.length === 0) {
      return { conflicts: false, changedFields: [] };
    }
    const anyDeclaredFieldAddedOrRemovedOrChanged = declaredChangedFields.some(field => {
      const inBase = Object.prototype.hasOwnProperty.call(baseFields, field);
      const inFresh = Object.prototype.hasOwnProperty.call(freshFields, field);
      if (!inBase && !inFresh) return false;
      if (inBase !== inFresh) return true;
      return canonicalizeSourceFieldValue(baseFields[field]) !== canonicalizeSourceFieldValue(freshFields[field]);
    });
    if (!anyDeclaredFieldAddedOrRemovedOrChanged) {
      return { conflicts: false, changedFields: [] };
    }
  }

  const hasTraceableSourceContext =
    (Array.isArray(decision.sourceIds) && decision.sourceIds.some(id => typeof id === 'string' && id.trim().length > 0)) ||
    (Array.isArray(input.freshSourceIds) && input.freshSourceIds.some(id => typeof id === 'string' && id.trim().length > 0)) ||
    (input.baselineSourceRevision !== undefined &&
      input.freshSourceRevision !== undefined &&
      String(input.baselineSourceRevision) !== String(input.freshSourceRevision));

  const effectiveChangedFields = Array.from(
    new Set([
      ...measuredDiffFields,
      ...(measuredDiffFields.length > 0 || hasTraceableSourceContext ? declaredChangedFields : []),
    ]),
  );

  if (effectiveChangedFields.length === 0) {
    return { conflicts: false, changedFields: [] };
  }

  const reason = typeof decision.reason === 'string' && decision.reason.trim().length > 0
    ? decision.reason.trim()
    : undefined;
  return {
    conflicts: true,
    changedFields: effectiveChangedFields,
    ...(reason ? { reason } : {}),
  };
}

/**
 * Returns true ONLY when traceable source/field changes AND an explicit
 * conflict decision supersede a prior factual correction. Free-text prose
 * wording changes in `baselineEvidence` vs `freshEvidence` return `false`.
 */
export function doesEvidenceConflictWithBaseline(
  baselineEvidence: unknown,
  freshEvidence: unknown,
  options?: Omit<EvidenceSupersessionInput, 'baselineEvidence' | 'freshEvidence'>,
): boolean {
  return evaluateEvidenceSupersession({
    baselineEvidence,
    freshEvidence,
    ...options,
  }).conflicts;
}

/**
 * Upserts a user-approved factual correction (`resolution === 'apply'` on a
 * non-verified finding) into the durable versioned ledger. Acknowledgements
 * (`isVerifiedSanityCategory`) and ignored findings (`resolution === 'ignore'`)
 * are strictly excluded.
 */
export function upsertApprovedFactualCorrection(
  ledger: ApprovedFactualCorrectionsLedgerV1 | undefined,
  args: {
    identity: string;
    finding: import('../types/siteRag').SanityFinding;
    userNote?: string;
    applyDirective?: import('../types/siteRag').SanityFinding['applyDirective'];
    approvedAt?: string;
    approvedByUid?: string;
    originReportId?: string;
    lastEvaluationStatus?: 'queued' | 'resolved';
    lastEvaluatedAt?: string;
    lastAppliedReportId?: string;
  },
): ApprovedFactualCorrectionsLedgerV1 | undefined {
  const {
    identity,
    finding,
    userNote,
    applyDirective,
    approvedAt = new Date().toISOString(),
    approvedByUid,
    originReportId,
    lastEvaluationStatus,
    lastEvaluatedAt,
    lastAppliedReportId,
  } = args;
  if (isVerifiedSanityCategory(finding.category)) {
    return ledger;
  }
  const directiveText =
    userNote?.trim() ||
    applyDirective?.suggestionAtResolution?.trim() ||
    finding.suggestion?.trim() ||
    finding.finding.trim();
  if (!directiveText) return ledger;

  const prevEntries = ledger?.entries ?? {};
  const existing = prevEntries[identity];
  const nextEntry: ApprovedFactualCorrectionEntryV1 = {
    identity,
    version: (existing?.version ?? 0) + 1,
    section: finding.section || existing?.section || 'General',
    category: finding.category || existing?.category || 'inconsistency',
    finding: finding.finding || existing?.finding || directiveText,
    appliedDirective: directiveText,
    ...(userNote && userNote.trim().length > 0 ? { userNote: userNote.trim() } : {}),
    ...(applyDirective ? { applyDirective } : {}),
    ...(finding.evidence
      ? { evidenceBaseline: finding.evidence }
      : existing?.evidenceBaseline
        ? { evidenceBaseline: existing.evidenceBaseline }
        : {}),
    ...(finding.evidenceSourceFields
      ? { evidenceSourceFields: { ...finding.evidenceSourceFields } }
      : existing?.evidenceSourceFields
        ? { evidenceSourceFields: { ...existing.evidenceSourceFields } }
        : {}),
    ...(finding.evidenceSourceIds
      ? { evidenceSourceIds: [...finding.evidenceSourceIds] }
      : existing?.evidenceSourceIds
        ? { evidenceSourceIds: [...existing.evidenceSourceIds] }
        : {}),
    ...(finding.evidenceSourceRevision !== undefined
      ? { evidenceSourceRevision: finding.evidenceSourceRevision }
      : existing?.evidenceSourceRevision !== undefined
        ? { evidenceSourceRevision: existing.evidenceSourceRevision }
        : {}),
    approvedAt,
    ...(approvedByUid ? { approvedByUid } : existing?.approvedByUid ? { approvedByUid: existing.approvedByUid } : {}),
    ...(originReportId
      ? { originReportId }
      : existing?.originReportId
        ? { originReportId: existing.originReportId }
        : {}),
    lastEvaluationStatus:
      lastEvaluationStatus ??
      (existing?.status === 'active' && existing.lastEvaluationStatus === 'resolved'
        ? 'resolved'
        : 'queued'),
    ...(lastEvaluatedAt
      ? { lastEvaluatedAt }
      : existing?.status === 'active' && existing.lastEvaluatedAt
        ? { lastEvaluatedAt: existing.lastEvaluatedAt }
        : {}),
    ...(lastAppliedReportId
      ? { lastAppliedReportId }
      : existing?.status === 'active' && existing.lastAppliedReportId
        ? { lastAppliedReportId: existing.lastAppliedReportId }
        : {}),
    status: 'active',
  };

  return {
    schemaVersion: APPROVED_FACTUAL_CORRECTIONS_SCHEMA_VERSION,
    revision: (ledger?.revision ?? 0) + 1,
    updatedAt: approvedAt,
    entries: {
      ...prevEntries,
      [identity]: nextEntry,
    },
  };
}

/**
 * Withdraws an entry in the approved factual corrections ledger when a user
 * clears a finding's resolution or switches it to `ignore`.
 *
 * CRITICAL INVARIANT:
 * Clearing a correction MUST persist an explicit `status: 'withdrawn'` marker
 * rather than deleting the key, so the withdrawal overrides any frozen report
 * ledger on reload (`overlaySanityDecisionMap`) and across multi-generation
 * reruns (`V1 -> V2 -> V3`).
 */
export function removeApprovedFactualCorrection(
  ledger: ApprovedFactualCorrectionsLedgerV1 | undefined,
  identity: string,
  updatedAt: string = new Date().toISOString(),
  fallbackMeta?: {
    section?: string;
    category?: string;
    finding?: string;
    appliedDirective?: string;
    userNote?: string;
    originReportId?: string;
    withdrawnByUid?: string;
  },
): ApprovedFactualCorrectionsLedgerV1 | undefined {
  const existing = ledger?.entries?.[identity];
  if (!existing && !fallbackMeta) {
    return ledger;
  }
  const category = existing?.category ?? fallbackMeta?.category ?? 'inconsistency';
  if (isVerifiedSanityCategory(category)) {
    return ledger;
  }
  if (
    existing &&
    existing.status === 'withdrawn' &&
    existing.withdrawnAt === updatedAt
  ) {
    return ledger;
  }

  const prevEntries = ledger?.entries ?? {};
  const withdrawnEntry: ApprovedFactualCorrectionEntryV1 = existing
    ? {
        ...existing,
        version: existing.version + 1,
        status: 'withdrawn',
        lastEvaluationStatus: 'withdrawn',
        lastEvaluatedAt: updatedAt,
        withdrawnAt: updatedAt,
        ...(fallbackMeta?.withdrawnByUid ? { withdrawnByUid: fallbackMeta.withdrawnByUid } : {}),
      }
    : {
        identity,
        version: 1,
        section: fallbackMeta?.section || 'General',
        category,
        finding: fallbackMeta?.finding || identity,
        appliedDirective: fallbackMeta?.appliedDirective || fallbackMeta?.finding || '',
        ...(fallbackMeta?.userNote ? { userNote: fallbackMeta.userNote } : {}),
        approvedAt: updatedAt,
        ...(fallbackMeta?.originReportId ? { originReportId: fallbackMeta.originReportId } : {}),
        lastEvaluatedAt: updatedAt,
        lastEvaluationStatus: 'withdrawn',
        status: 'withdrawn',
        withdrawnAt: updatedAt,
        ...(fallbackMeta?.withdrawnByUid ? { withdrawnByUid: fallbackMeta.withdrawnByUid } : {}),
      };

  return {
    schemaVersion: APPROVED_FACTUAL_CORRECTIONS_SCHEMA_VERSION,
    revision: (ledger?.revision ?? 0) + 1,
    updatedAt,
    entries: {
      ...prevEntries,
      [identity]: withdrawnEntry,
    },
  };
}

/**
 * Builds the `prevSanityResolutions` array for a rerun (`V1 -> V2 -> V3`) by
 * combining:
 * 1. Current `sanityFindings` decisions (`apply` on non-verified findings, and
 *    `ignore` on ignored/acknowledged findings), AND
 * 2. Durable `approvedFactualCorrections` entries with `status === 'active'`
 *    from earlier reruns (e.g. a correction approved in V1 that resolved the
 *    finding in V2 so it was no longer in `V2.sanityFindings`, but must still
 *    be enforced when generating V3).
 *
 * Explicitly excludes any finding or ledger entry marked `withdrawn`.
 */
export function collectDurableSanityResolutionsForRerun(
  report: {
    sanityFindings?: readonly import('../types/siteRag').SanityFinding[];
    approvedFactualCorrections?: ApprovedFactualCorrectionsLedgerV1;
  } | undefined,
  identityFn?: (f: { finding?: unknown; category?: unknown; section?: unknown }) => string,
): Array<{
  finding: string;
  resolution: 'apply' | 'ignore';
  userNote?: string;
  applyDirective?: import('../types/siteRag').SanityFinding['applyDirective'];
}> {
  if (!report) return [];
  const out: Array<{
    finding: string;
    resolution: 'apply' | 'ignore';
    userNote?: string;
    applyDirective?: import('../types/siteRag').SanityFinding['applyDirective'];
  }> = [];
  const seenIdentities = new Set<string>();
  const seenFindingTexts = new Set<string>();
  const withdrawnIdentities = new Set<string>();
  const withdrawnFindingTexts = new Set<string>();

  const ledgerMap = report.approvedFactualCorrections?.entries ?? {};
  for (const [entryKey, entry] of Object.entries(ledgerMap)) {
    if (entry.status === 'withdrawn') {
      withdrawnIdentities.add(entry.identity || entryKey);
      if (entry.finding) withdrawnFindingTexts.add(entry.finding);
    }
  }

  for (const f of report.sanityFindings ?? []) {
    const idKey = identityFn ? identityFn(f) : `${f.section}::${f.category}::${f.finding}`;
    const ledgerEntry = ledgerMap[idKey];
    if (f.correctionLineage?.status === 'withdrawn') {
      withdrawnIdentities.add(idKey);
      withdrawnFindingTexts.add(f.finding);
      continue;
    }
    // If a finding on this report has `resolution === undefined` while the ledger entry
    // was never yet applied to a rerun (`lastEvaluationStatus === 'queued'`), the user cleared
    // the finding on this report.
    if (f.resolution === undefined && !f.correctionLineage && ledgerEntry?.lastEvaluationStatus === 'queued') {
      withdrawnIdentities.add(idKey);
      withdrawnFindingTexts.add(f.finding);
      continue;
    }
    if (f.resolution !== 'apply' && f.resolution !== 'ignore') continue;
    // If the ledger has a newer or equal withdrawal marker, it overrides a stale frozen `'apply'`.
    if (
      f.resolution === 'apply' &&
      ledgerEntry?.status === 'withdrawn' &&
      (!f.resolvedAt || (ledgerEntry.withdrawnAt && ledgerEntry.withdrawnAt >= f.resolvedAt))
    ) {
      withdrawnIdentities.add(idKey);
      withdrawnFindingTexts.add(f.finding);
      continue;
    }
    // Verified findings (`risk-verified` / `strength-verified`) are never
    // treated as `apply` factual corrections even if legacy data had `apply`.
    const effectiveResolution: 'apply' | 'ignore' =
      f.resolution === 'apply' && isVerifiedSanityCategory(f.category)
        ? 'ignore'
        : f.resolution;
    seenIdentities.add(idKey);
    seenFindingTexts.add(f.finding);
    out.push({
      finding: f.finding,
      resolution: effectiveResolution,
      userNote: f.userNote,
      applyDirective: effectiveResolution === 'apply' ? f.applyDirective : undefined,
    });
  }

  for (const entry of Object.values(ledgerMap)) {
    if (entry.status !== 'active') continue;
    if (isVerifiedSanityCategory(entry.category)) continue;
    if (withdrawnIdentities.has(entry.identity) || withdrawnFindingTexts.has(entry.finding)) continue;
    if (seenIdentities.has(entry.identity) || seenFindingTexts.has(entry.finding)) continue;
    seenIdentities.add(entry.identity);
    seenFindingTexts.add(entry.finding);
    out.push({
      finding: entry.finding,
      resolution: 'apply',
      userNote: entry.userNote,
      applyDirective: entry.applyDirective ?? {
        section: entry.section,
        suggestionAtResolution: entry.appliedDirective,
      },
    });
  }

  return out;
}

export interface AppliedCorrectionsSummary {
  appliedCount: number;
  resolvedCount: number;
  stillFailingCount: number;
  supersededCount?: number;
  items: Array<{
    identity: string;
    section: string;
    category: string;
    finding: string;
    appliedDirective: string;
    status: 'resolved' | 'still-failing' | 'superseded-by-evidence';
    remainingReason?: string;
    supersededByEvidence?: string;
    changedSourceFields?: string[];
    conflictReason?: string;
  }>;
}

export interface ReconcileSanityFindingsArgs<
  T extends import('../types/siteRag').SanityFinding = import('../types/siteRag').SanityFinding,
> {
  prior: readonly T[] | undefined;
  fresh: readonly T[];
  identity: (item: T) => string;
  evaluatedAt?: string;
  fromReportId?: string;
  /** Durable ledger of approved factual corrections from the parent report (`V1 -> V2 -> V3`). */
  priorApprovedCorrections?: ApprovedFactualCorrectionsLedgerV1;
  /** Optional explicit conflict decisions keyed by finding identity. */
  evidenceConflictDecisions?: Record<string, EvidenceConflictDecisionV1>;
}

export interface ReconcileSanityFindingsResult<
  T extends import('../types/siteRag').SanityFinding = import('../types/siteRag').SanityFinding,
> {
  mergedFindings: T[];
  audit: PreservedAudit[];
  orphanCount: number;
  appliedCorrectionsSummary?: AppliedCorrectionsSummary;
  approvedFactualCorrections?: ApprovedFactualCorrectionsLedgerV1;
}

/**
 * Reconciles `SanityFinding` decisions across a report rerun (`V2 -> V3`).
 *
 * - `resolution === 'ignore'` is a standing acknowledgment and carries forward
 *   onto matching fresh findings via `mergePreservedUserDecisions`.
 * - `resolution === 'apply'` was queued for the rerun and consumed by
 *   `applyDirectives`. It must NOT remain on the new report as
 *   `resolution: 'apply'` (`Queued` / "Will apply on rerun"). Instead:
 *     * If the finding no longer fires in `fresh`, it is recorded as
 *       `status: 'resolved'` in `appliedCorrectionsSummary`.
 *     * If the finding still fires in `fresh`, it stays unresolved/active
 *       (`resolution: undefined`) and receives `correctionLineage` with
 *       `status: 'applied-still-failing'`, the applied directive text, and the
 *       concrete remaining failure reason (`fresh.finding`).
 *     * It transitions to `status: 'superseded-by-evidence'` ONLY when
 *       traceable source/field changes AND an explicit conflict decision
 *       are present (`evaluateEvidenceSupersession`).
 * - Withdrawn corrections (`status: 'withdrawn'`) are preserved as withdrawal
 *   markers in `approvedFactualCorrections` across generations so they stay
 *   withdrawn on `V1 -> V2 -> V3` and are never re-applied.
 */
export function reconcileSanityFindingsAcrossRerun<
  T extends import('../types/siteRag').SanityFinding = import('../types/siteRag').SanityFinding,
>(args: ReconcileSanityFindingsArgs<T>): ReconcileSanityFindingsResult<T> {
  const {
    prior,
    fresh,
    identity,
    evaluatedAt = new Date().toISOString(),
    fromReportId,
    priorApprovedCorrections,
    evidenceConflictDecisions,
  } = args;

  const freshArr: T[] = fresh.map(f => ({ ...f }));
  const hasPriorFindings = Boolean(prior && prior.length > 0);
  const hasPriorLedger = Boolean(
    priorApprovedCorrections && Object.keys(priorApprovedCorrections.entries ?? {}).length > 0,
  );
  if (!hasPriorFindings && !hasPriorLedger) {
    return { mergedFindings: freshArr, audit: [], orphanCount: 0 };
  }

  // Partition prior items by resolution kind.
  // Verified findings (`risk-verified` / `strength-verified`) are positive confirmations;
  // even if marked `apply` in legacy data, treat them as acknowledgements (`ignore`),
  // never as factual corrections.
  const ignoredPrior = (prior ?? []).filter(
    p => p.resolution === 'ignore' || (p.resolution === 'apply' && isVerifiedSanityCategory(p.category)),
  );
  const ignoredIdentitySet = new Set<string>(ignoredPrior.map(p => identity(p)));

  const withdrawnEntriesByKey = new Map<string, ApprovedFactualCorrectionEntryV1>();
  if (priorApprovedCorrections?.entries) {
    for (const [entryKey, entry] of Object.entries(priorApprovedCorrections.entries)) {
      const key = entry.identity || entryKey;
      if (entry.status === 'withdrawn') {
        withdrawnEntriesByKey.set(key, entry);
      } else if (ignoredIdentitySet.has(key) && !isVerifiedSanityCategory(entry.category)) {
        withdrawnEntriesByKey.set(key, {
          ...entry,
          version: (entry.version ?? 1) + 1,
          status: 'withdrawn',
          lastEvaluationStatus: 'withdrawn',
          lastEvaluatedAt: evaluatedAt,
          withdrawnAt: evaluatedAt,
        });
      }
    }
  }

  for (const p of prior ?? []) {
    const key = identity(p);
    const ledgerEntry = priorApprovedCorrections?.entries?.[key];
    if (p.correctionLineage?.status === 'withdrawn') {
      if (ledgerEntry && ledgerEntry.status !== 'withdrawn') {
        withdrawnEntriesByKey.set(key, {
          ...ledgerEntry,
          version: (ledgerEntry.version ?? 1) + 1,
          status: 'withdrawn',
          lastEvaluationStatus: 'withdrawn',
          lastEvaluatedAt: evaluatedAt,
          withdrawnAt: p.correctionLineage.appliedAt || evaluatedAt,
        });
      } else if (!withdrawnEntriesByKey.has(key) && !isVerifiedSanityCategory(p.category)) {
        withdrawnEntriesByKey.set(key, {
          identity: key,
          version: 1,
          section: p.section || 'General',
          category: p.category || 'inconsistency',
          finding: p.finding,
          appliedDirective: p.correctionLineage.appliedDirective || p.suggestion || p.finding,
          approvedAt: p.correctionLineage.appliedAt || evaluatedAt,
          ...(fromReportId ? { originReportId: fromReportId } : {}),
          lastEvaluatedAt: evaluatedAt,
          lastEvaluationStatus: 'withdrawn',
          status: 'withdrawn',
          withdrawnAt: p.correctionLineage.appliedAt || evaluatedAt,
        });
      }
    } else if (
      p.resolution === undefined &&
      !p.correctionLineage &&
      ledgerEntry &&
      ledgerEntry.status === 'active' &&
      ledgerEntry.lastEvaluationStatus === 'queued'
    ) {
      // Queued on the prior report and then cleared on `prior` findings before rerun.
      withdrawnEntriesByKey.set(key, {
        ...ledgerEntry,
        version: (ledgerEntry.version ?? 1) + 1,
        status: 'withdrawn',
        lastEvaluationStatus: 'withdrawn',
        lastEvaluatedAt: evaluatedAt,
        withdrawnAt: evaluatedAt,
      });
    }
  }

  // Build unified map of active approved factual corrections from:
  // 1) priorApprovedCorrections ledger (carried across V1 -> V2 -> V3), and
  // 2) prior sanityFindings with resolution === 'apply' (non-verified).
  interface EffectiveAppliedCorrection {
    key: string;
    section: string;
    category: string;
    finding: string;
    directiveText: string;
    userNote?: string;
    applyDirective?: import('../types/siteRag').SanityFinding['applyDirective'];
    evidenceBaseline?: string;
    evidenceSourceFields?: Record<string, string | number | boolean | null>;
    evidenceSourceIds?: string[];
    evidenceSourceRevision?: string | number;
    approvedAt: string;
    approvedByUid?: string;
    originReportId?: string;
    version: number;
    fromDirectPriorFinding: boolean;
  }

  const effectiveAppliedByKey = new Map<string, EffectiveAppliedCorrection>();

  if (priorApprovedCorrections?.entries) {
    for (const [entryKey, entry] of Object.entries(priorApprovedCorrections.entries)) {
      if (entry.status !== 'active') continue;
      if (isVerifiedSanityCategory(entry.category)) continue;
      const key = entry.identity || entryKey;
      if (ignoredIdentitySet.has(key) || withdrawnEntriesByKey.has(key)) continue;
      effectiveAppliedByKey.set(key, {
        key,
        section: entry.section,
        category: entry.category,
        finding: entry.finding,
        directiveText: entry.appliedDirective,
        userNote: entry.userNote,
        applyDirective: entry.applyDirective,
        evidenceBaseline: entry.evidenceBaseline,
        evidenceSourceFields: entry.evidenceSourceFields,
        evidenceSourceIds: entry.evidenceSourceIds,
        evidenceSourceRevision: entry.evidenceSourceRevision,
        approvedAt: entry.approvedAt,
        approvedByUid: entry.approvedByUid,
        originReportId: entry.originReportId ?? fromReportId,
        version: entry.version ?? 1,
        fromDirectPriorFinding: false,
      });
    }
  }

  for (const p of prior ?? []) {
    if (p.resolution === 'apply' && !isVerifiedSanityCategory(p.category)) {
      const key = identity(p);
      const withdrawnEntry = withdrawnEntriesByKey.get(key);
      if (
        withdrawnEntry &&
        (!p.resolvedAt || (withdrawnEntry.withdrawnAt && withdrawnEntry.withdrawnAt >= p.resolvedAt))
      ) {
        continue;
      }
      // If the user explicitly re-approved after a prior withdrawal, remove from withdrawnEntriesByKey.
      if (withdrawnEntry) {
        withdrawnEntriesByKey.delete(key);
      }
      const existing = effectiveAppliedByKey.get(key);
      const directiveText =
        p.userNote?.trim() ||
        p.applyDirective?.suggestionAtResolution?.trim() ||
        p.suggestion ||
        existing?.directiveText ||
        '';
      effectiveAppliedByKey.set(key, {
        key,
        section: p.section || existing?.section || 'General',
        category: p.category || existing?.category || 'inconsistency',
        finding: p.finding || existing?.finding || directiveText,
        directiveText,
        userNote: p.userNote ?? existing?.userNote,
        applyDirective: p.applyDirective ?? existing?.applyDirective,
        evidenceBaseline: p.evidence ?? existing?.evidenceBaseline,
        evidenceSourceFields: p.evidenceSourceFields ?? existing?.evidenceSourceFields,
        evidenceSourceIds: p.evidenceSourceIds ?? existing?.evidenceSourceIds,
        evidenceSourceRevision: p.evidenceSourceRevision ?? existing?.evidenceSourceRevision,
        approvedAt: p.resolvedAt ?? existing?.approvedAt ?? evaluatedAt,
        approvedByUid: existing?.approvedByUid,
        originReportId: existing?.originReportId ?? fromReportId,
        version: existing ? existing.version + 1 : 1,
        fromDirectPriorFinding: true,
      });
    }
  }

  let orphanCount = 0;
  const [afterIgnoreMerge, audit] = mergePreservedUserDecisions({
    prior: ignoredPrior as unknown as Array<Record<string, unknown>>,
    fresh: freshArr as unknown as Array<Record<string, unknown>>,
    identity: f => identity(f as unknown as T),
    preserveFields: ['resolution', 'userNote', 'resolvedAt'] as const,
    onOrphan: () => {
      orphanCount += 1;
    },
  });

  const matchedAppliedFreshByKey = new Map<
    string,
    {
      finding: T;
      supersededByEvidence?: string;
      changedSourceFields?: string[];
      conflictReason?: string;
    }
  >();

  const mergedFindings: T[] = (afterIgnoreMerge as unknown as T[]).map(f => {
    const key = identity(f);
    const appliedPrior = effectiveAppliedByKey.get(key);
    if (!appliedPrior || f.resolution === 'ignore') {
      return f;
    }
    const explicitDecision = f.evidenceConflictDecision ?? evidenceConflictDecisions?.[key];
    const supersession = evaluateEvidenceSupersession({
      baselineEvidence: appliedPrior.evidenceBaseline,
      freshEvidence: f.evidence,
      baselineSourceFields: appliedPrior.evidenceSourceFields,
      freshSourceFields: f.evidenceSourceFields,
      baselineSourceIds: appliedPrior.evidenceSourceIds,
      freshSourceIds: f.evidenceSourceIds,
      baselineSourceRevision: appliedPrior.evidenceSourceRevision,
      freshSourceRevision: f.evidenceSourceRevision,
      conflictDecision: explicitDecision,
    });

    if (supersession.conflicts) {
      const freshEv =
        (typeof f.evidence === 'string' && f.evidence.trim().length > 0
          ? f.evidence.trim()
          : supersession.reason) ||
        `Source fields changed: ${supersession.changedFields.join(', ')}`;
      matchedAppliedFreshByKey.set(key, {
        finding: f,
        supersededByEvidence: freshEv,
        changedSourceFields: supersession.changedFields,
        conflictReason: supersession.reason,
      });
      return {
        ...f,
        resolution: undefined,
        applyDirective: undefined,
        correctionLineage: {
          status: 'superseded-by-evidence',
          ...(appliedPrior.directiveText ? { appliedDirective: appliedPrior.directiveText } : {}),
          ...(appliedPrior.userNote ? { userNote: appliedPrior.userNote } : {}),
          appliedAt: evaluatedAt,
          ...(fromReportId ? { fromReportId } : {}),
          remainingReason: f.finding,
          supersededByEvidence: freshEv,
          changedSourceFields: supersession.changedFields,
          ...(supersession.reason ? { conflictReason: supersession.reason } : {}),
        },
      };
    }

    matchedAppliedFreshByKey.set(key, { finding: f });
    return {
      ...f,
      resolution: undefined,
      applyDirective: undefined,
      correctionLineage: {
        status: 'applied-still-failing',
        ...(appliedPrior.directiveText ? { appliedDirective: appliedPrior.directiveText } : {}),
        ...(appliedPrior.userNote ? { userNote: appliedPrior.userNote } : {}),
        appliedAt: evaluatedAt,
        ...(fromReportId ? { fromReportId } : {}),
        remainingReason: f.finding,
      },
    };
  });

  let appliedCorrectionsSummary: AppliedCorrectionsSummary | undefined;
  const nextLedgerEntries: Record<string, ApprovedFactualCorrectionEntryV1> = {};

  // Preserve any previously-superseded or withdrawn entries in the ledger history.
  if (priorApprovedCorrections?.entries) {
    for (const [k, entry] of Object.entries(priorApprovedCorrections.entries)) {
      const key = entry.identity || k;
      if (withdrawnEntriesByKey.has(key)) continue;
      if (entry.status !== 'active' && !ignoredIdentitySet.has(key)) {
        nextLedgerEntries[key] = entry;
      }
    }
  }
  for (const [key, withdrawnEntry] of withdrawnEntriesByKey.entries()) {
    nextLedgerEntries[key] = withdrawnEntry;
  }

  if (effectiveAppliedByKey.size > 0) {
    const items: AppliedCorrectionsSummary['items'] = [];
    for (const [key, appliedPrior] of effectiveAppliedByKey.entries()) {
      const matched = matchedAppliedFreshByKey.get(key);
      if (matched) {
        const matchedFresh = matched.finding;
        if (matched.supersededByEvidence) {
          items.push({
            identity: key,
            section: matchedFresh.section || appliedPrior.section,
            category: matchedFresh.category || appliedPrior.category,
            finding: matchedFresh.finding,
            appliedDirective: appliedPrior.directiveText,
            status: 'superseded-by-evidence',
            remainingReason: matchedFresh.finding,
            supersededByEvidence: matched.supersededByEvidence,
            ...(matched.changedSourceFields ? { changedSourceFields: matched.changedSourceFields } : {}),
            ...(matched.conflictReason ? { conflictReason: matched.conflictReason } : {}),
          });
          nextLedgerEntries[key] = {
            identity: key,
            version: appliedPrior.version,
            section: matchedFresh.section || appliedPrior.section,
            category: matchedFresh.category || appliedPrior.category,
            finding: appliedPrior.finding,
            appliedDirective: appliedPrior.directiveText,
            ...(appliedPrior.userNote ? { userNote: appliedPrior.userNote } : {}),
            ...(appliedPrior.applyDirective ? { applyDirective: appliedPrior.applyDirective } : {}),
            ...(appliedPrior.evidenceBaseline ? { evidenceBaseline: appliedPrior.evidenceBaseline } : {}),
            ...(appliedPrior.evidenceSourceFields ? { evidenceSourceFields: appliedPrior.evidenceSourceFields } : {}),
            ...(appliedPrior.evidenceSourceIds ? { evidenceSourceIds: appliedPrior.evidenceSourceIds } : {}),
            ...(appliedPrior.evidenceSourceRevision !== undefined
              ? { evidenceSourceRevision: appliedPrior.evidenceSourceRevision }
              : {}),
            approvedAt: appliedPrior.approvedAt,
            ...(appliedPrior.approvedByUid ? { approvedByUid: appliedPrior.approvedByUid } : {}),
            ...(appliedPrior.originReportId ? { originReportId: appliedPrior.originReportId } : {}),
            ...(fromReportId ? { lastAppliedReportId: fromReportId } : {}),
            lastEvaluatedAt: evaluatedAt,
            lastEvaluationStatus: 'superseded-by-evidence',
            status: 'superseded-by-evidence',
            supersededReason: matchedFresh.finding,
            supersededByEvidence: matched.supersededByEvidence,
            ...(matched.changedSourceFields ? { changedSourceFields: matched.changedSourceFields } : {}),
            ...(matched.conflictReason ? { conflictReason: matched.conflictReason } : {}),
          };
        } else {
          items.push({
            identity: key,
            section: matchedFresh.section || appliedPrior.section,
            category: matchedFresh.category || appliedPrior.category,
            finding: matchedFresh.finding,
            appliedDirective: appliedPrior.directiveText,
            status: 'still-failing',
            remainingReason: matchedFresh.finding,
          });
          nextLedgerEntries[key] = {
            identity: key,
            version: appliedPrior.version,
            section: matchedFresh.section || appliedPrior.section,
            category: matchedFresh.category || appliedPrior.category,
            finding: appliedPrior.finding,
            appliedDirective: appliedPrior.directiveText,
            ...(appliedPrior.userNote ? { userNote: appliedPrior.userNote } : {}),
            ...(appliedPrior.applyDirective ? { applyDirective: appliedPrior.applyDirective } : {}),
            ...(appliedPrior.evidenceBaseline ? { evidenceBaseline: appliedPrior.evidenceBaseline } : {}),
            ...(appliedPrior.evidenceSourceFields ? { evidenceSourceFields: appliedPrior.evidenceSourceFields } : {}),
            ...(appliedPrior.evidenceSourceIds ? { evidenceSourceIds: appliedPrior.evidenceSourceIds } : {}),
            ...(appliedPrior.evidenceSourceRevision !== undefined
              ? { evidenceSourceRevision: appliedPrior.evidenceSourceRevision }
              : {}),
            approvedAt: appliedPrior.approvedAt,
            ...(appliedPrior.approvedByUid ? { approvedByUid: appliedPrior.approvedByUid } : {}),
            ...(appliedPrior.originReportId ? { originReportId: appliedPrior.originReportId } : {}),
            ...(fromReportId ? { lastAppliedReportId: fromReportId } : {}),
            lastEvaluatedAt: evaluatedAt,
            lastEvaluationStatus: 'still-failing',
            status: 'active',
          };
        }
      } else {
        if (appliedPrior.fromDirectPriorFinding) {
          orphanCount += 1;
        }
        items.push({
          identity: key,
          section: appliedPrior.section,
          category: appliedPrior.category,
          finding: appliedPrior.finding,
          appliedDirective: appliedPrior.directiveText,
          status: 'resolved',
        });
        nextLedgerEntries[key] = {
          identity: key,
          version: appliedPrior.version,
          section: appliedPrior.section,
          category: appliedPrior.category,
          finding: appliedPrior.finding,
          appliedDirective: appliedPrior.directiveText,
          ...(appliedPrior.userNote ? { userNote: appliedPrior.userNote } : {}),
          ...(appliedPrior.applyDirective ? { applyDirective: appliedPrior.applyDirective } : {}),
          ...(appliedPrior.evidenceBaseline ? { evidenceBaseline: appliedPrior.evidenceBaseline } : {}),
          ...(appliedPrior.evidenceSourceFields ? { evidenceSourceFields: appliedPrior.evidenceSourceFields } : {}),
          ...(appliedPrior.evidenceSourceIds ? { evidenceSourceIds: appliedPrior.evidenceSourceIds } : {}),
          ...(appliedPrior.evidenceSourceRevision !== undefined
            ? { evidenceSourceRevision: appliedPrior.evidenceSourceRevision }
            : {}),
          approvedAt: appliedPrior.approvedAt,
          ...(appliedPrior.approvedByUid ? { approvedByUid: appliedPrior.approvedByUid } : {}),
          ...(appliedPrior.originReportId ? { originReportId: appliedPrior.originReportId } : {}),
          ...(fromReportId ? { lastAppliedReportId: fromReportId } : {}),
          lastEvaluatedAt: evaluatedAt,
          lastEvaluationStatus: 'resolved',
          status: 'active',
        };
      }
    }
    const resolvedCount = items.filter(i => i.status === 'resolved').length;
    const stillFailingCount = items.filter(i => i.status === 'still-failing').length;
    const supersededCount = items.filter(i => i.status === 'superseded-by-evidence').length;
    appliedCorrectionsSummary = {
      appliedCount: items.length,
      resolvedCount,
      stillFailingCount,
      ...(supersededCount > 0 ? { supersededCount } : {}),
      items,
    };
  }

  const approvedFactualCorrections: ApprovedFactualCorrectionsLedgerV1 | undefined =
    Object.keys(nextLedgerEntries).length > 0
      ? {
          schemaVersion: APPROVED_FACTUAL_CORRECTIONS_SCHEMA_VERSION,
          revision: (priorApprovedCorrections?.revision ?? 0) + 1,
          updatedAt: evaluatedAt,
          entries: nextLedgerEntries,
        }
      : undefined;

  return {
    mergedFindings,
    audit,
    orphanCount,
    ...(appliedCorrectionsSummary ? { appliedCorrectionsSummary } : {}),
    ...(approvedFactualCorrections ? { approvedFactualCorrections } : {}),
  };
}
